import React from 'react';
import { Copy, Modal, StringHelpers } from '@salesperformanceint/e4shared';
import { useSelector } from 'react-redux';
import config from '../../../config';
import { apiCode, apiConfig } from '../../../services';
let mime = require('mime-types');

export const extension = f => (f?.name ? f.name.slice(f.name.lastIndexOf('.')) : '');

export const assetIcon = asset => {
    switch (asset.source) {
        case 'file':
        case 'google':
        case 'onedrive':
            if (asset.size === 0 && asset.ext) return fileIconFromServiceId(asset.ext);
            return fileIcon(extension(asset));
        case 'url':
            return 'globe';
        case 'highspot':
            return fileIcon('.' + mime.extension(asset.ext));
        case 'accelerate':
        case 'challenger':
        case 'docebo':
        case 'gtm':
        case 'highspot-dl':
        case 'learning360':
        case 'lessonly':
        case 'pulse':
        case 'showpad':
            return 'graduation-cap';
        case 'content-pitchback':
        case 'pitchback':
            return 'video-camera';
        case 'content-kb':
        case 'kb':
            return 'bank';
        case 'blog':
        case 'content-blog':
            return 'comments';
        case 'powerpoint':
            return 'file-powerpoint';
        case 'gong':
            return 'microphone';
        case 'youtube':
            return 'youtube-play';
        case 'content-richardson':
            if (asset.type === 'text') return 'text';
            if (asset.type === 'url') return 'link';
            switch (asset?.asset?.ext || asset?.ext) {
                case '.txt':
                    return 'text';
                case '.url':
                    return 'link';
                default:
                    return fileIcon(asset?.asset?.ext || asset?.ext || '');
            }
        default:
            return 'file';
    }
};

export const fileIconFromServiceId = service => {
    switch (service.toLowerCase()) {
        case 'doc':
            return 'file-word';
        case 'spread':
            return 'file-excel';
        case 'sheet':
            return 'file-powerpoint';
        default:
            console.warn(`Missing service - ${service}`);
            return 'file';
    }
};

export const fileIcon = ext => {
    switch (ext.toLowerCase()) {
        case '.doc':
        case '.docx':
            return 'file-word';
        case '.xls':
        case '.xlsx':
            return 'file-excel';
        case '.ppt':
        case '.pptx':
            return 'file-powerpoint';
        case '.pdf':
            return 'file-pdf';
        case '.jpg':
        case '.jpeg':
        case '.png':
        case '.gif':
            return 'file-image';
        case '.mp3':
        case '.wav':
            return 'file-sound';
        case '.avi':
        case '.mov':
        case '.mp4':
        case '.mp4s':
            return 'file-movie';
        case '.zip':
            return 'file-archive';
        default:
            return 'file';
    }
};

export const contentTypeIcon = type => {
    if (type.indexOf('video') >= 0) return 'file-movie';
    if (type.indexOf('audio') >= 0) return 'file-sound';
    if (type.indexOf('pdf') >= 0) return 'file-pdf';
    if (type.indexOf('word') >= 0) return 'file-word';
    if (type.indexOf('powerpoint') >= 0) return 'file-powerpoint';
    if (type.indexOf('excel') >= 0) return 'file-excel';
    return 'file';
};

export const openHighSpotAsset = (asset, guid) => {
    const url = asset.id ? `download-highspot/${asset.id}` : `download-highspot-raw?json=${escape(JSON.stringify(asset))}`;
    const ext = mime.extension(asset.ext);

    const params = { ...apiConfig(guid, null, true), method: 'GET' };
    fetch(`${config.apiGateway.URL}${url}${apiCode()}`, params)
        .then(response => {
            const blob = response.blob(`${asset.safeName}.${ext}`, { type: asset.ext });
            return blob;
        })
        .then(blob => {
            if (['mp4s', 'zip'].some(x => x === ext)) {
                const anchor = document.createElement('a');
                anchor.download = `${asset.safeName}.${ext}`;
                anchor.href = URL.createObjectURL(blob);
                anchor.dataset.downloadurl = [asset.ext, anchor.download, anchor.href].join(':');
                anchor.click();
            } else {
                const newWindow = window.open('/loading');
                newWindow.onload = () => {
                    newWindow.location = URL.createObjectURL(blob);
                };
            }
        })
        .catch(err => {
            console.error(err);
        });
};

export const openLearning360Asset = (asset, user) => {
    const integration = user.integrations.find(i => i.ref === '360L');
    if (integration) {
        const baseUrl = integration.extra;
        const newWindow = window.open('/loading', 'l360');
        newWindow.onload = () => {
            newWindow.location = `${baseUrl}/course/play/${asset.internal_Id}`;
        };
    }
};

export const openURLAsset = (asset, sameWindow) => {
    if (sameWindow) {
        window.location = assetLink(asset);
    } else {
        const newWindow = window.open('/loading');
        newWindow.onload = () => {
            newWindow.location = assetLink(asset);
        };
    }
};

export const openFromStorage = (folder, path, name, user) => {
    fetch(`${config.apiGateway.URL}download-file/${folder}/${path}${apiCode()}`, apiConfig(user.guid, null, true))
        .then(response => {
            const blob = response.blob(name);
            if (blob) {
                blob.type = response.headers['content-type'];
                blob.filename = name; // doesn't work!
            }
            return blob;
        })
        .then(blob => {
            const newWindow = window.open('/loading');
            URL.createObjectURL(blob);
            newWindow.onload = () => {
                newWindow.location = URL.createObjectURL(blob);
            };
        })
        .catch(err => {
            // console.log(err);
        });
};

export const assetLink = asset => {
    switch (asset.source) {
        case 'file':
            if (asset.url.indexOf('https') > -1) return asset.url;

            // this is the old way and should be removed once we know all urls are fully formed
            return `${config.storage.URL}${config.environment}/${asset.url}`;
        case 'youtube':
            return `https://www.youtube.com/watch?v=${asset.internal_Id}`;
        case 'gong':
            return `https://app.gong.io/call?id=${asset.internal_Id}`;
        case 'content-richardson':
            if (asset.ext === '.txt') {
                return null;
            }
            if (asset.ext === '.url') {
                return asset?.url;
            }

            return `${config.storage.URL}${asset.url}`;

        default:
            return asset?.url;
    }
};

export const Assets = ({ assets, onDelete, list }) => {
    if (!Array.isArray(assets) || assets.length === 0) return null;

    const user = useSelector(state => state.user.user);

    const [showAsset, setShowAsset] = React.useState(null);

    assets = assets.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

    const AssetRow = ({ asset }) => {
        const source = asset.source;
        asset = asset.childAsset ? asset.childAsset : asset.asset ? asset.asset : asset;
        asset.source = source;
        let icon = asset.icon ?? assetIcon(asset);
        if (source === 'content-richardson') {
            if (asset.type === 'url') icon = 'link';
            if (asset.type === 'text') icon = 'text';
        }

        return (
            <div className="flex flex-center mb5">
                <i className={`fa fa-${icon} mr5`} />
                <span className="ellipsis500 flex-grow">
                    <a
                        data-testid="asset-link"
                        href={assetLink(asset)}
                        target={['accelerate', 'learn2'].some(x => x === asset.source) ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        onClick={
                            asset.source === 'content-richardson' && asset.ext === '.txt'
                                ? e => {
                                      e.preventDefault();
                                      setShowAsset(asset);
                                  }
                                : asset.source === 'highspot'
                                  ? e => {
                                        e.preventDefault();
                                        openHighSpotAsset(asset, user.guid);
                                    }
                                  : asset.source === 'learning360'
                                    ? e => {
                                          e.preventDefault();
                                          openLearning360Asset(asset, user);
                                      }
                                    : asset.source === 'learning360'
                                      ? e => {
                                            e.preventDefault();
                                            openLearning360Asset(asset, user);
                                        }
                                      : asset.source === 'content-pitchback' || asset.source === 'pitchback'
                                        ? e => {
                                              e.preventDefault();
                                              const folder = 'task_completion';
                                              const start = asset.url.indexOf(folder) + folder.length + 1;
                                              const end = asset.url.indexOf('?');
                                              const file = asset.url.substring(start, end);
                                              openFromStorage(folder, file, 'pitchback', user);
                                          }
                                        : null
                        }
                    >
                        {asset.name.replace(/^https?:\/\//gi, '')}
                        {['file', 'highspot'].some(s => s === asset.source) && (
                            <span> - {StringHelpers.bytesToSize(asset.size)}</span>
                        )}
                    </a>
                </span>
                {onDelete && (
                    <i
                        className="ml5 text-danger fa fa-trash pointer"
                        data-testid={`btn-delete-asset-${asset.id}`}
                        onClick={() => onDelete(asset)}
                    />
                )}
                <Modal show={showAsset} close={() => setShowAsset(null)} title={showAsset?.name} footer={null}>
                    <Copy value={showAsset?.description} />
                </Modal>
            </div>
        );
    };

    return (
        <div className={list ? 'asset-list' : ''}>
            {assets.map((a, i) => (
                <div key={`asset-${i}`}>
                    <AssetRow asset={a} />
                </div>
            ))}
        </div>
    );
};

export default Assets;
